import React, { useState, useEffect } from 'react';
import { ChevronRight } from 'lucide-react';
import { useAmplitude, useTrackOnMount } from '../../providers/AmplitudeProvider';
import { AmplitudeEvent } from '../../constants/amplitudeEvents';

interface AgeSelectionProps {
    onSelect: (ageGroup: string) => void;
}

const ageOptions = [
    { id: '18-29', label: 'Age: 18-29', image: 'https://funnel-cdn.b-cdn.net/images/age-18-29.png' },
    { id: '30-39', label: 'Age: 30-39', image: 'https://funnel-cdn.b-cdn.net/images/age-30-39.png' },
    { id: '40-49', label: 'Age: 40-49', image: 'https://funnel-cdn.b-cdn.net/images/age-40-49.png' },
    { id: '50+', label: 'Age: 50+', image: 'https://funnel-cdn.b-cdn.net/images/age-50-plus.png' },
];

const AgeSelection: React.FC<AgeSelectionProps> = ({ onSelect }) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const { trackEvent } = useAmplitude();

    useTrackOnMount([
        { event: AmplitudeEvent.QUIZ_STARTED, properties: { funnel_type: 'long', screen_name: 'age_selection', utm_source: localStorage.getItem('utm_source') ?? 'direct', utm_medium: localStorage.getItem('utm_medium') ?? null, utm_campaign: localStorage.getItem('utm_campaign') ?? null, utm_content: localStorage.getItem('utm_content') ?? null, utm_term: localStorage.getItem('utm_term') ?? null } },
        { event: AmplitudeEvent.QUESTION_VIEWED, properties: { step_number: 1, screen_name: 'age_selection', utm_source: localStorage.getItem('utm_source') ?? 'direct', utm_medium: localStorage.getItem('utm_medium') ?? null, utm_campaign: localStorage.getItem('utm_campaign') ?? null, utm_content: localStorage.getItem('utm_content') ?? null, utm_term: localStorage.getItem('utm_term') ?? null } },
    ]);

    const handleSelect = (optionId: string) => {
        setSelectedOption(optionId);
        trackEvent(AmplitudeEvent.QUESTION_ANSWERED, {
            step_number: 1,
            screen_name: 'age_selection',
            age_range: optionId,
        });
        setTimeout(() => {
            onSelect(optionId);
        }, 500);
    };

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="py-[13.5px] px-4 bg-white border-b border-[#17171714] mb-3">
                <div className="flex items-center justify-center lg:justify-start lg:max-w-7xl lg:mx-auto">
                    <span className="font-poppins text-[24px] tracking-[-2px]">
                        Move <span className="font-semibold">Together</span>
                    </span>
                </div>
            </header>

            <div className="flex flex-col px-4">
                {/* Title */}
                <div className="text-center mb-4">
                    <h1 className="text-[26px]/[30px] font-semibold text-[#111827] uppercase">
                        Personalized Walking Plan
                    </h1>
                    <p className="text-[14px] text-[#898989] font-semibold uppercase mt-3 tracking-[1px]">
                        Based on your age
                    </p>
                </div>

                {/* Age Options Grid */}
                <div className="grid grid-cols-2 gap-2 max-w-lg mx-auto w-full">
                    {ageOptions.map((option) => {
                        const isSelected = selectedOption === option.id;
                        return (
                            <button
                                key={option.id}
                                onClick={() => handleSelect(option.id)}
                                className={`relative mt-2 bg-[#F1F2F5] rounded-[15px] overflow-hidden flex flex-col pb-8 transition-colors ${isSelected ? 'ring-2 ring-[#369FFF]' : ''}`}
                            >
                                <div className="flex-1 flex items-end justify-center pt-2">
                                    <img
                                        src={option.image}
                                        alt={option.label}
                                        className="w-[109px] h-auto"
                                        width={109}
                                        height={150}
                                        loading="eager"
                                        decoding="async"
                                        fetchPriority="high"
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                </div>
                                <div className="absolute bottom-0 left-0 right-0">
                                    <div className="bg-[#369FFF] text-white pl-[10px] pr-[14px] py-[5px] flex items-center justify-between">
                                        <span className="font-medium text-[16px]">{option.label}</span>
                                        <div className='flex items-center justify-center w-5 h-5 bg-[#FFFFFFE5] rounded-full'>
                                            <ChevronRight className="w-3.5 h-3.5 text-[#369FFF]" />
                                        </div>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="px-3 py-6 mt-2 text-center">
                <p className="text-[15px] font-medium text-[#2D2D2D]">
                    By choosing your age and continuing you agree to our{' '}
                    <a
                        href="https://move-club.co/terms/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#369FFF] font-semibold underline"
                    >
                        Terms of Service
                    </a>
                    {' | '}
                    <a
                        href="https://move-club.co/privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#369FFF] font-semibold underline"
                    >
                        Privacy Policy.
                    </a>
                </p>
                <p className="text-[15px] font-medium text-[#2D2D2D]">
                    Please review before continuing.
                </p>
            </div>
        </div>
    );
};

export default AgeSelection;
